import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useDomainStore, usePersonStoriesStore } from '@/hooks/useStores';
import ToneCallout from '@/components/memories/ToneCallout';
import MemoryCard from '@/components/person/MemoryCard';
import StoryForm, { type StoryFormValues } from '@/components/person/StoryForm';
import Plus from '@/icons/Plus';
import {
  canCreateStoriesForMember,
  canEditOrDeleteStories,
  canEditOrDeleteStory,
  type UpdateStoryPayload
} from '@/types/story';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';
import { getRoleDisplayName } from '@/utils/rolePermissions';

import './person-stories.css';

interface PersonStoriesProps {
  familyId: string;
  familyMemberId: string;
  firstName: string;
  readonly?: boolean;
  effectiveRole?: string | null;
  canModifyMember?: boolean;
  sectionMode?: 'tab-panel';
}

const PersonStories: React.FC<PersonStoriesProps> = observer(
  ({
    familyId,
    familyMemberId,
    firstName,
    readonly = false,
    effectiveRole = null,
    canModifyMember = false,
    sectionMode = 'tab-panel'
  }) => {
    const { t } = useTranslation('forms');
    const domainStore = useDomainStore();
    const personStoriesStore = usePersonStoriesStore();
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [upgradeHint, setUpgradeHint] = useState<string | null>(null);

    const canCreate = canCreateStoriesForMember(canModifyMember, effectiveRole);
    // Fallback for API builds that do not yet resolve access per memory.
    const memberEditFallback = canEditOrDeleteStories(canModifyMember, effectiveRole);
    const isOwner = isFamilyOwner(domainStore.currentFamily?.userRole);
    const roleLabel = effectiveRole ? getRoleDisplayName(effectiveRole) : t('person.memberFallback');

    useEffect(() => {
      personStoriesStore.loadStoriesForFamilyMember(familyId, familyMemberId);
    }, [familyId, familyMemberId, personStoriesStore]);

    const stories = personStoriesStore.getStoriesForFamilyMember(familyMemberId);
    const isLoading = personStoriesStore.isLoadingForFamilyMember(familyMemberId);
    const displayError = personStoriesStore.getErrorForFamilyMember(familyMemberId);

    const handleCancelForm = () => {
      setIsAdding(false);
      setEditingId(null);
      setFormError(null);
      setUpgradeHint(null);
    };

    const handleCreate = async (values: StoryFormValues) => {
      setIsSaving(true);
      setFormError(null);
      setUpgradeHint(null);
      const result = await personStoriesStore.createStory(familyId, {
        familyMemberId,
        title: values.title,
        content: values.content,
        mediaAssetIds: values.mediaAssetIds
      });
      setIsSaving(false);
      if (result.success) {
        handleCancelForm();
      } else {
        const parts = getUpgradeAwareErrorParts(result.error, isOwner, t('person.saveMemoryFailed'));
        setFormError(parts.message);
        setUpgradeHint(parts.upgradeHint);
      }
    };

    const handleUpdate = async (storyId: string, values: StoryFormValues) => {
      setIsSaving(true);
      setFormError(null);
      const payload: UpdateStoryPayload = {
        title: values.title,
        content: values.content,
        mediaAssetIds: values.mediaAssetIds
      };
      const result = await personStoriesStore.updateStory(familyId, storyId, payload);
      setIsSaving(false);
      if (result.success) {
        handleCancelForm();
      } else {
        setFormError(result.error?.getUserMessage?.() || result.error?.message || t('person.updateMemoryFailed'));
      }
    };

    const handleDelete = async (storyId: string) => {
      if (!window.confirm(t('confirm.deleteMemory'))) return;
      const result = await personStoriesStore.deleteStory(familyId, storyId);
      if (!result.success) {
        personStoriesStore.setError(
          familyMemberId,
          result.error?.getUserMessage?.() || result.error?.message || t('person.deleteMemoryFailed')
        );
      }
    };

    const editingStory = editingId ? personStoriesStore.getStoryById(editingId) : undefined;

    if (isLoading) {
      return (
        <div className="person-stories">
          <div className="person-stories__loading">{t('person.loadingMemories')}</div>
        </div>
      );
    }

    const panelBody = (
      <div className="person-stories__body">
        {displayError && (
          <div className="person-stories__error" role="alert">
            <span>{displayError}</span>
            <button
              type="button"
              className="person-stories__error-dismiss"
              onClick={() => personStoriesStore.setError(familyMemberId, null)}
              aria-label={t('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        {readonly && (
          <div className="person-stories__readonly-banner" role="status">
            {t('person.memoriesReadonlyBefore')} <strong>{roleLabel}</strong>. {t('person.memoriesReadonlyAfter')}
          </div>
        )}

        {canCreate && !readonly && <ToneCallout firstName={firstName} userId={domainStore.user?.id} />}

        {canCreate && !readonly && stories.length > 0 && !isAdding && !editingId && (
          <button type="button" className="person-stories__add-trigger" onClick={() => setIsAdding(true)}>
            <span className="person-stories__add-trigger-icon" aria-hidden>
              <Plus width={16} height={16} />
            </span>
            <span>
              <span className="person-stories__add-trigger-primary">
                {t('person.shareMemory', { name: firstName })}
              </span>
              <span className="person-stories__add-trigger-secondary">{t('person.shareMemorySecondary')}</span>
            </span>
          </button>
        )}

        {(isAdding || editingId) && (
          <StoryForm
            familyId={familyId}
            familyMemberId={familyMemberId}
            isEditing={!!editingId}
            isSaving={isSaving}
            errorMessage={formError}
            upgradeHint={upgradeHint}
            initialValues={
              editingStory
                ? {
                    title: editingStory.title,
                    content: editingStory.content,
                    mediaAssetIds: editingStory.mediaAttachments.map((a) => a.mediaAssetId),
                    mediaMeta: Object.fromEntries(
                      editingStory.mediaAttachments.map((a) => [
                        a.mediaAssetId,
                        { mimeType: a.mimeType, filename: a.originalFilename }
                      ])
                    )
                  }
                : undefined
            }
            onCancel={handleCancelForm}
            onSubmit={(values) => (editingId ? handleUpdate(editingId, values) : handleCreate(values))}
          />
        )}

        {stories.length === 0 && !isAdding && (
          <div className="person-stories__empty">
            <h3 className="person-stories__empty-title">{t('person.emptyMemoriesTitle', { name: firstName })}</h3>
            <p className="person-stories__empty-body">{t('person.emptyMemoriesBody')}</p>
            {canCreate && !readonly && (
              <button type="button" className="btn btn--primary" onClick={() => setIsAdding(true)}>
                {t('person.shareFirstMemory')}
              </button>
            )}
          </div>
        )}

        {stories.length > 0 && (
          <div className="person-stories__list">
            {stories.map((story) =>
              editingId === story.id ? null : (
                <MemoryCard
                  key={story.id}
                  story={story}
                  familyId={familyId}
                  canEdit={canEditOrDeleteStory(story, memberEditFallback)}
                  canDelete={canEditOrDeleteStory(story, memberEditFallback)}
                  onEdit={() => {
                    setEditingId(story.id);
                    setIsAdding(false);
                  }}
                  onDelete={() => handleDelete(story.id)}
                />
              )
            )}
          </div>
        )}
      </div>
    );

    if (sectionMode === 'tab-panel') {
      return (
        <div className="person-stories person-stories--tab-panel">
          <div className="tab-panel-card__head">
            <h2 className="tab-panel-card__title">{t('person.tabs.memories')}</h2>
          </div>
          <div className="tab-panel-card__body">{panelBody}</div>
        </div>
      );
    }

    return <div className="person-stories">{panelBody}</div>;
  }
);

export default PersonStories;
